import { useMutation, useQueryClient } from "@tanstack/react-query";
import { socialApi } from "@bw/shared/api";
import type { ConversationResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { INLINE_GUARDIAN_PROMPT } from "../../../common/guardian/denials";

/**
 * Get-or-create a 1:1 conversation (Slice F3, WRITE). Posts
 * `createConversation([friendId])` with the CLICKED friend's user_id and returns
 * the server's `ConversationResponse` — whose `conversation_id` is an independent
 * uuid (the backend B1 is get-or-create for a 1:1, so the id is NOT the friend's
 * user_id). The caller navigates to that RETURNED conversation_id, never the
 * friend's uid.
 *
 * On success it invalidates `social.conversations()` so the DM inbox preview shows
 * the (possibly newly created) conversation as the SERVER's truth — no optimistic
 * insert. A rejected create (403 blocked / 500 / network) does NOT navigate; it
 * surfaces on `mutation.error` for the page to show honestly, with the friend row
 * left in place.
 *
 * The variable is the friend's user_id (a string), mirroring `useSendMessage`'s
 * `<Response, unknown, string>` shape so the page can scope per-row pending via
 * `mutation.variables`.
 *
 * Stale-error honesty (mirrors useFriendActions / useChallengeActions): this
 * mutation's error is folded into the FriendsPage's SHARED actionError banner, so
 * a settled create-failure must not outlive a later SUCCESSFUL inbox action. When
 * this create STARTS it clears any settled error on the inbox verbs (via the
 * optional `onActionStart`); the inbox verbs symmetrically reset this one when
 * THEY start — so the combined banner only ever reflects the action currently in
 * flight / most recently settled. `onActionStart` defaults to a no-op so the hook
 * stays standalone; the page passes a ref-stable callback that resets the four
 * inbox verbs.
 */
export function useCreateConversation(onActionStart: () => void = () => {}) {
  const queryClient = useQueryClient();

  return useMutation<ConversationResponse, unknown, string>({
    meta: INLINE_GUARDIAN_PROMPT,
    mutationFn: (friendId: string) =>
      socialApi.createConversation([friendId]),
    onMutate: () => {
      // A new Message starts — clear any settled error on the inbox verbs so a
      // prior accept/decline/cancel/unfriend failure can't keep showing in the
      // shared banner once this create's own state takes over.
      onActionStart();
    },
    onSuccess: () => {
      // The DM inbox refetches so a newly created conversation appears as the
      // server's truth (never an optimistic phantom).
      void queryClient.invalidateQueries({
        queryKey: queryKeys.social.conversations(),
      });
    },
  });
}
